import { useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Video, Users, Contact, LogOut, ArrowLeft, LayoutTemplate, ShieldCheck, Church, HeartHandshake, UsersRound, ClipboardList, Route } from "lucide-react";
import { AdminPastoral } from "@/components/staff/AdminPastoral";
import { PastoralRequests } from "@/components/staff/PastoralRequests";
import { AdminCommunities } from "@/components/staff/AdminCommunities";
import { LeaderRegistrations } from "@/components/staff/LeaderRegistrations";
import { AdminJourney } from "@/components/staff/AdminJourney";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { PasscodeGate } from "@/components/staff/PasscodeGate";
import { StaffCalendar } from "@/components/staff/StaffCalendar";
import { DevotionalManager } from "@/components/staff/DevotionalManager";
import { VisitorLog } from "@/components/staff/VisitorLog";
import { StaffDirectory } from "@/components/staff/StaffDirectory";
import { ContentEditor } from "@/components/staff/ContentEditor";
import { AdminUsers } from "@/components/staff/AdminUsers";
import { ChangePasswordDialog } from "@/components/staff/ChangePasswordDialog";
import { getRole, getUser, clearSession } from "@/lib/api";

const TABS = [
  { id: "calendar", label: "Calendar", icon: CalendarDays, C: StaffCalendar },
  { id: "pastoral-requests", label: "Pastoral Requests", icon: HeartHandshake, C: PastoralRequests, admin: true },
  { id: "devotionals", label: "Devotionals", icon: Video, C: DevotionalManager },
  { id: "visitors", label: "Visitors", icon: Users, C: VisitorLog },
  { id: "team", label: "Team & Notifications", icon: Contact, C: StaffDirectory },
  { id: "content", label: "Website Pages", icon: LayoutTemplate, C: ContentEditor, admin: true },
  { id: "admins", label: "Administrators", icon: ShieldCheck, C: AdminUsers, admin: true },
  { id: "pastoral", label: "Pastoral & Schedules", icon: Church, C: AdminPastoral, admin: true },
  { id: "journey", label: "Journey", icon: Route, C: AdminJourney },
  { id: "communities", label: "Communities", icon: UsersRound, C: AdminCommunities, admin: true },
  { id: "registrations", label: "Community Registrations", icon: ClipboardList, C: LeaderRegistrations, admin: true, leader: true },
];

export default function StaffHub() {
  const [role, setRole] = useState(getRole());
  const [user, setUser] = useState(getUser());
  const logout = () => { clearSession(); setRole(""); setUser(null); };
  const tabs = role === "leader" ? TABS.filter((t) => t.leader) : TABS.filter((t) => !t.admin || role === "admin");

  if (!role) return <PasscodeGate onSuccess={(r, u) => { setRole(r); setUser(u); }} />;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="staff-hub-page">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 lg:px-10">
          <div className="flex min-w-0 items-center gap-2 sm:gap-4">
            <Logo className="h-6 sm:h-9" />
            <span className="whitespace-nowrap rounded-full bg-[#EBF3FA] px-2.5 py-1 text-[11px] font-semibold text-[#0A3A6B] sm:px-3 sm:text-xs" data-testid="staff-role-badge">
              <span className="hidden sm:inline">Staff Hub · </span>{user ? `@${user.username}${role === "leader" ? " · Community leader" : ""}` : "Staff"}
            </span>
          </div>
          <div className="flex items-center gap-1">
            {user && <ChangePasswordDialog />}
            <Button asChild variant="ghost" size="sm" data-testid="staff-back-to-site"><Link to="/"><ArrowLeft className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Site</span></Link></Button>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="staff-logout-button"><LogOut className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Lock</span></Button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-5 py-8 lg:px-10">
        <Tabs defaultValue={tabs[0].id}>
          <TabsList className="h-auto flex-wrap justify-start gap-1 rounded-2xl bg-white p-1.5 shadow-sm">
            {tabs.map(({ id, label, icon: Icon }) => (
              <TabsTrigger key={id} value={id} data-testid={`staff-tab-${id}`}
                className="rounded-xl px-4 py-2 data-[state=active]:bg-[#0A3A6B] data-[state=active]:text-white">
                <Icon className="mr-2 h-4 w-4" />{label}
              </TabsTrigger>
            ))}
          </TabsList>
          {tabs.map(({ id, C }) => (
            <TabsContent key={id} value={id} className="mt-6"><C role={role} /></TabsContent>
          ))}
        </Tabs>
      </main>
    </div>
  );
}
